// Office > Holds (rule 30; plan fleet-registry plans/2026-11-03-app-quality-holds-register.md): every QC Hold,
// raw material and finished goods, with what it waits on, its age and the value held. It replaces QA's RM and
// FG hold registers (Excel). Read from stored events (GET /holds): what D365 shows this minute, and every
// action on a hold, stay on the Goods in QC Holds panel, where each row links.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { fieldClass, labelClass, officeFocus, useMyGrants, useResource } from '../../shells/OfficeShell';
import { PutOnHold, useHoldConfig } from './BatchHolds';
import { TONE_CLASS, chipClass, stamp } from './goodsInModel';
import { CLASS_LABEL, SCOPE_LABEL, WAITING_LABEL, daysText, money, valueHeldText, type RegisterPayload, type RegisterRow, type WaitingOn } from './holdsModel';

const col = createColumnHelper<RegisterRow>();

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="flex min-w-[11rem] flex-1 flex-col gap-(--s1) rounded-(--radius) border border-(--border) bg-(--card) p-(--s3)">
      <span className={labelClass}>{label}</span>
      <span className="font-display text-[28px] font-semibold leading-none tabular-nums">{value}</span>
      {note ? <span className="text-(length:--fs-sm) text-(--muted)">{note}</span> : null}
    </div>
  );
}

export function Holds() {
  const [status, setStatus] = useState<'open' | 'all'>('open');
  const [waiting, setWaiting] = useState<WaitingOn | 'ANY'>('ANY');
  const res = useResource<RegisterPayload>(`/holds?status=${status}`);
  const holdConfig = useHoldConfig();
  const grants = useMyGrants();
  const [adding, setAdding] = useState(false);
  const mayAdd = holdConfig?.enabled === true && grants.known && (grants.holds('RELEASE') || grants.holds('RECEIVE'));
  const t = res.data?.totals ?? null;
  const rows = (res.data?.rows ?? []).filter((r) => waiting === 'ANY' || r.waitingOn === waiting);

  const columns = [
    col.accessor((r) => r.holdId, {
      id: 'hold',
      header: 'Hold',
      cell: (c) => (
        <Link to={`/office/goods-in?hold=${c.getValue()}`} className={`underline ${officeFocus}`}>
          #{c.getValue()}
        </Link>
      ),
      meta: { rowHeader: true, nowrap: true },
    }),
    col.accessor((r) => `${r.item} ${r.itemName ?? ''}`, {
      id: 'item',
      header: 'Item',
      cell: (c) => (
        <>
          <span className="font-mono">{c.row.original.item}</span> {c.row.original.itemName ?? ''}
          <div className="text-(length:--fs-sm) text-(--muted)">
            {CLASS_LABEL[c.row.original.materialClass]} · {SCOPE_LABEL[c.row.original.scope]}
          </div>
        </>
      ),
    }),
    col.accessor((r) => r.batch, { id: 'batch', header: 'Batch', meta: { mono: true, nowrap: true } }),
    col.accessor((r) => r.supplier?.name ?? r.supplier?.account ?? '', {
      id: 'supplier',
      header: 'Supplier',
      cell: (c) => c.getValue() || (c.row.original.materialClass === 'FG' ? 'Our production' : 'not known'),
    }),
    col.accessor((r) => r.reason, {
      id: 'reason',
      header: 'Reason',
      cell: (c) => <span title={c.getValue()}>{c.getValue().length > 90 ? `${c.getValue().slice(0, 90)}…` : c.getValue()}</span>,
    }),
    col.accessor((r) => r.ncrId, { id: 'ncr', header: 'NCR', meta: { mono: true, nowrap: true } }),
    col.accessor((r) => r.requestedAt, {
      id: 'asked',
      header: 'Asked',
      cell: (c) => (
        <>
          {stamp(c.getValue())}
          <div className="text-(length:--fs-sm) text-(--muted)">{c.row.original.requestedByUpn}</div>
        </>
      ),
    }),
    col.accessor((r) => r.daysOpen, { id: 'age', header: 'Age', cell: (c) => daysText(c.getValue()), meta: { align: 'right', nowrap: true } }),
    col.accessor((r) => r.waitingOn, {
      id: 'waiting',
      header: 'Waiting on',
      cell: (c) => {
        const w = c.getValue();
        const tone = w === 'DRIFT' ? 'bad' : w === 'DONE' ? 'ok' : w === 'DECISION' ? 'warn' : 'quiet';
        return <span className={`${chipClass} w-fit ${TONE_CLASS[tone]}`}>{WAITING_LABEL[w]}</span>;
      },
    }),
    col.accessor((r) => r.lastSeen?.words ?? '', {
      id: 'seen',
      header: 'D365 last seen',
      cell: (c) => (c.row.original.lastSeen ? `${c.getValue()} (${stamp(c.row.original.lastSeen.at ?? '')})` : 'not read yet'),
    }),
    col.accessor((r) => r.value?.physical ?? -1, {
      id: 'value',
      header: 'Value held',
      cell: (c) => (c.row.original.waitingOn === 'DONE' ? '—' : money(c.row.original.value?.physical)),
      meta: { align: 'right', nowrap: true },
    }),
  ];

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Holds</h1>
        <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
          Every QC Hold, raw material and finished goods: how old it is, what it is waiting on and what it is worth. Open a hold
          to see what D365 shows now, set or release it.
        </p>
        {mayAdd && !adding ? (
          <Button variant="outline" type="button" className="mt-(--s2)" onClick={() => setAdding(true)}>
            Put batch on QC Hold
          </Button>
        ) : null}
        {adding ? (
          <PutOnHold
            config={holdConfig}
            source={{ kind: 'FREE', id: null }}
            prefill={{ lines: [], description: '' }}
            grants={grants}
            onDone={() => res.reload()}
            startOpen
            onClose={() => setAdding(false)}
          />
        ) : null}
      </header>

      {t ? (
        <div className="flex flex-wrap gap-(--s3)" aria-label="Holds at a glance">
          <Tile label="Open holds" value={String(t.open)} note={`${t.byClass.RM} raw material · ${t.byClass.FG} finished goods`} />
          <Tile label="Open over 14 days" value={String(t.over14)} />
          <Tile
            label="Value held"
            value={valueHeldText(t)}
            note={`RM ${money(t.valueHeld.RM)} · FG ${money(t.valueHeld.FG)}${t.valueAsOf ? ` · stock as of ${stamp(t.valueAsOf)}` : ''}`}
          />
          <Tile
            label="Waiting on"
            value={String(t.waitingOn.DECISION)}
            note={`QA decision · ${t.waitingOn.D365_HOLD + t.waitingOn.D365_RELEASE} on a D365 step${t.waitingOn.DRIFT ? ` · ${t.waitingOn.DRIFT} taken off outside the app` : ''}`}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-(--s3)">
        <label className="flex w-fit flex-col gap-(--s1)">
          <span className={labelClass}>Show</span>
          <select className={`${fieldClass} ${officeFocus}`} value={status} onChange={(e) => setStatus(e.target.value as 'open' | 'all')}>
            <option value="open">Open</option>
            <option value="all">All, released included</option>
          </select>
        </label>
        <label className="flex w-fit flex-col gap-(--s1)">
          <span className={labelClass}>Waiting on</span>
          <select className={`${fieldClass} ${officeFocus}`} value={waiting} onChange={(e) => setWaiting(e.target.value as WaitingOn | 'ANY')}>
            <option value="ANY">Anything</option>
            {(Object.keys(WAITING_LABEL) as WaitingOn[]).map((w) => (
              <option key={w} value={w}>
                {WAITING_LABEL[w]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {res.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">Could not read the holds: {res.error} This is not the same as "none".</p>
          <Button variant="outline" type="button" onClick={res.reload}>
            Try again
          </Button>
        </div>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          caption="QC Holds, oldest open first"
          getRowId={(r) => String(r.holdId)}
          rowAccent={(r) => (r.waitingOn === 'DRIFT' || (r.waitingOn !== 'DONE' && r.ageBucket === 'OVER_14') ? 'bad' : null)}
          loading={res.loading}
          stickyFirst
          search={(res.data?.rows.length ?? 0) > 10 ? 'Search item, batch, supplier or NCR' : undefined}
          empty={res.data ? (status === 'open' ? 'No open holds.' : 'No holds recorded yet.') : null}
        />
      )}
    </div>
  );
}

export default Holds;
